import "./App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  matchPath,
  useLocation,
} from "react-router-dom";
import { ThemeProvider } from "./providers/ThemeProvider";
import { useAuthContext } from "./hooks/useAuthContext";
import { useState, Suspense, useEffect } from "react";
import { Toaster } from "sonner";
import { DataLoadingProvider } from "./contexts/DataLoadingContext";
import useMediaQuery from "./hooks/useMediaQuery";
import { lazyWithRetry } from "./utils/chunkLoadRecovery";

// Lazy load all page components
const Login = lazyWithRetry(() => import("./pages/Login/Login"));
const Signup = lazyWithRetry(() => import("./pages/Signup/Signup_NO"));
const VerifyEmail = lazyWithRetry(() => import("./pages/Signup/VerifyEmail"));
const PasswordRecovery = lazyWithRetry(() => import("./pages/Recover/Recover"));
const ResetPassword = lazyWithRetry(() => import("./pages/Recover/ResetPassword"));
const AffiliatePage = lazyWithRetry(() => import("./pages/Affiliate/AffiliatePage"));
const AffiliateSuccess = lazyWithRetry(() =>
  import("./pages/Affiliate/AffiliateSuccess")
);
const FunnelBuilder = lazyWithRetry(() => import("./pages/Funnels/FunnelBuilder"));
const CheckoutBuilder = lazyWithRetry(() => import("./pages/CheckoutBuilder"));
const HomePageBuilder = lazyWithRetry(
  () => import("./pages/Ecommerce/homePages/HomePageBuilder")
);
const SalesPageBuilder = lazyWithRetry(() => import("./pages/SalesPages/SalesPageBuilder"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/Help/PrivacyPolicy"));
const EmployeeSignup = lazyWithRetry(() =>
  import("./pages/Companies/Employees/EmployeeSignup")
);
const AcceptInvite = lazyWithRetry(() =>
  import("./pages/Companies/Employees/AcceptInvite")
);
const AccountAccessConsent = lazyWithRetry(() =>
  import("./pages/AccountAccess/Consent")
);

// Loading component for Suspense fallback
const PageLoading = () => <div />;

function AppRoutes() {
  const { authIsReady } = useAuthContext();

  if (!authIsReady) {
    return null;
  }

  return (
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <div className="App flex flex-col sm:flex-row min-h-screen h-full">
        <Toaster position="top-center" duration={6000} richColors closeButton />
        <BrowserRouter>
          <LayoutWrapper />
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

// Separate component that sits inside <BrowserRouter> so we can use hooks like useLocation
const LayoutWrapper = () => {
  const { user } = useAuthContext();
  const location = useLocation();
  const noLayoutRoutes = [
    "/__/auth/action",
    "/auth/action",
    "/password/reset",
    "/affiliate/:productId",
    "/affiliate/success",
    "/funnel/:funnelId",
    "/checkout-builder/:checkoutId",
    "/sales-page-builder/:pageId",
    "/home-page-builder/:pageId",
    "/content-page-builder/:pageId",
    "/home-page-template-builder/:templateId",
    "/accept-invite/:token",
    "/collaborator-signup",
    "/access/:code",
    "/verify-email",
  ];
  const isNoLayout = noLayoutRoutes.some((pattern) =>
    matchPath(pattern, location.pathname)
  );

  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 640px)");
  const isTablet = useMediaQuery("(max-width: 1024px)");

  const [rerender, setRerender] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(isDesktop);

  if (user) {
    return (
      <DataLoadingProvider user={user}>
        {isNoLayout ? (
          <Suspense fallback={<PageLoading />}>
            <Routes>
              <Route path="/__/auth/action" element={<ResetPassword />} />
              <Route path="/auth/action" element={<ResetPassword />} />
              <Route path="/password/reset" element={<ResetPassword />} />
              <Route path="/affiliate/:productId" element={<AffiliatePage />} />
              <Route path="/affiliate/success" element={<AffiliateSuccess />} />
              <Route path="/funnel/:funnelId" element={<FunnelBuilder />} />
              <Route
                path="/checkout-builder/:checkoutId"
                element={<CheckoutBuilder />}
              />
              <Route
                path="/sales-page-builder/:pageId"
                element={<SalesPageBuilder />}
              />
              <Route
                path="/home-page-builder/:pageId"
                element={<HomePageBuilder />}
              />
              <Route
                path="/content-page-builder/:pageId"
                element={<HomePageBuilder />}
              />
              <Route
                path="/home-page-template-builder/:templateId"
                element={<HomePageBuilder />}
              />
              <Route path="/accept-invite/:token" element={<AcceptInvite />} />
              <Route path="/collaborator-signup" element={<EmployeeSignup />} />
              <Route path="/access/:code" element={<AccountAccessConsent />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
            </Routes>
          </Suspense>
        ) : (
          <Suspense fallback={<PageLoading />}>
            <MainLayoutWithBanner
              isMobile={isMobile || isTablet}
              sidebarExpanded={sidebarExpanded}
              setSidebarExpanded={setSidebarExpanded}
              rerender={rerender}
              setRerender={setRerender}
            />
          </Suspense>
        )}
      </DataLoadingProvider>
    );
  } else {
    return (
      <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/password/recovery" element={<PasswordRecovery />} />
          <Route path="/__/auth/action" element={<ResetPassword />} />
          <Route path="/auth/action" element={<ResetPassword />} />
          <Route path="/password/reset" element={<ResetPassword />} />
          <Route path="/affiliate/:productId" element={<AffiliatePage />} />
          <Route path="/help/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />
          <Route path="/collaborator-signup" element={<EmployeeSignup />} />
          <Route path="/access/:code" element={<AccountAccessConsent />} />
          <Route path="*" element={<Login />} />
        </Routes>
      </Suspense>
    );
  }
};

const MainLayoutWithBanner = lazyWithRetry(() =>
  import("./components/MainLayoutWithBanner")
);

export default AppRoutes;
